import type { JSX } from "react";
import { useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { Check, ChevronDown } from "lucide-react";
import type { TaxonomyApiItem, TaxonomyKind } from "@recipevault/shared";
import { db } from "../../sync/db.js";
import { cn } from "../../shared/utils.js";
import type { LibraryFilters } from "./useRecipes.js";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/popover.js";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/ui/command.js";

interface Props {
  filters: LibraryFilters;
  onChange: (next: LibraryFilters) => void;
}

const KIND_LABELS: Record<TaxonomyKind, string> = {
  tag: "Tags",
  category: "Categories",
  cuisine: "Cuisines",
  diet: "Diet",
};

const KIND_TO_FILTER_KEY = {
  tag: "tagIds",
  category: "categoryIds",
  cuisine: "cuisineIds",
  diet: "dietFlags",
} as const satisfies Record<TaxonomyKind, keyof LibraryFilters>;

interface KindButtonProps {
  label: string;
  items: TaxonomyApiItem[];
  selectedIds: string[];
  onToggle: (id: string) => void;
}

function KindButton({ label, items, selectedIds, onToggle }: KindButtonProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const count = selectedIds.length;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`${label}${count > 0 ? ` (${String(count)} selected)` : ""}`}
          className={cn(
            "inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/50 transition-colors",
            count > 0
              ? "bg-emerald-700/30 border-emerald-600 text-emerald-200"
              : "bg-zinc-800 border-zinc-700 text-zinc-300 hover:border-zinc-600",
          )}
        >
          <span>{label}</span>
          {count > 0 && (
            <span className="rounded-full bg-emerald-900/60 px-1.5 py-0 text-[0.625rem] font-medium leading-4 tabular-nums">
              {count}
            </span>
          )}
          <ChevronDown className="h-3 w-3 opacity-70" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="start">
        <Command>
          <CommandInput placeholder={`Search ${label.toLowerCase()}…`} />
          <CommandList>
            <CommandEmpty>No matching {label.toLowerCase()}.</CommandEmpty>
            <CommandGroup>
              {items.map((item) => {
                const isSelected = selectedIds.includes(item.id);
                return (
                  <CommandItem
                    key={item.id}
                    value={item.name}
                    onSelect={() => { onToggle(item.id); }}
                  >
                    <span
                      className={cn(
                        "mr-2 inline-flex h-3 w-3 items-center justify-center",
                        isSelected ? "text-emerald-400" : "text-transparent",
                      )}
                      aria-hidden
                    >
                      <Check className="h-3 w-3" />
                    </span>
                    {item.name}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// Total-time quick-filter chips (Plan 13, Task 10). A fixed row, independent
// of taxonomy — every recipe has a totalTimeMin (or doesn't), so this always
// renders regardless of whether the household has any tags/categories set up.
const TIME_OPTIONS = [30, 45, 60] as const;

/**
 * Sets/clears/replaces the time cap without ever assigning `undefined` to
 * `maxTotalTimeMin` — exactOptionalPropertyTypes requires the key to be
 * *absent*, not present-with-undefined, to mean "no cap".
 */
function withMaxTotalTimeMin(filters: LibraryFilters, maxTotalTimeMin: number | null): LibraryFilters {
  const { tagIds, categoryIds, cuisineIds, dietFlags } = filters;
  if (maxTotalTimeMin === null) return { tagIds, categoryIds, cuisineIds, dietFlags };
  return { tagIds, categoryIds, cuisineIds, dietFlags, maxTotalTimeMin };
}

interface TimeChipsProps {
  value: number | undefined;
  onSelect: (mins: number) => void;
}

function TimeChips({ value, onSelect }: TimeChipsProps): JSX.Element {
  return (
    <div className="inline-flex items-center gap-1.5" role="group" aria-label="Total time">
      {TIME_OPTIONS.map((mins) => {
        const active = value === mins;
        return (
          <button
            key={mins}
            type="button"
            aria-pressed={active}
            onClick={() => { onSelect(mins); }}
            className={cn(
              "inline-flex min-h-12 items-center justify-center rounded-full border px-3 py-1 text-xs outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/50 transition-colors",
              active
                ? "bg-emerald-700/30 border-emerald-600 text-emerald-200"
                : "bg-zinc-800 border-zinc-700 text-zinc-300 hover:border-zinc-600",
            )}
          >
            {`≤${String(mins)}`}
          </button>
        );
      })}
    </div>
  );
}

export function ChipFilter({ filters, onChange }: Props): JSX.Element {
  const taxonomy = useLiveQuery(async () => db.taxonomy.toArray(), [], [] as TaxonomyApiItem[]);

  function toggle(kind: TaxonomyKind, id: string): void {
    const key = KIND_TO_FILTER_KEY[kind];
    const current = filters[key];
    const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
    onChange({ ...filters, [key]: next });
  }

  function selectTime(mins: number): void {
    onChange(withMaxTotalTimeMin(filters, filters.maxTotalTimeMin === mins ? null : mins));
  }

  const grouped: Record<TaxonomyKind, TaxonomyApiItem[]> = {
    tag: [],
    category: [],
    cuisine: [],
    diet: [],
  };
  for (const t of taxonomy ?? []) {
    if (!t.deletedAt) grouped[t.kind].push(t);
  }

  const anySelected =
    filters.tagIds.length +
      filters.categoryIds.length +
      filters.cuisineIds.length +
      filters.dietFlags.length >
    0 || filters.maxTotalTimeMin != null;

  // Hide a kind whose taxonomy table is empty — no point offering an empty filter.
  const visibleKinds = (Object.keys(KIND_LABELS) as TaxonomyKind[]).filter(
    (k) => grouped[k].length > 0,
  );

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {visibleKinds.map((kind) => (
        <KindButton
          key={kind}
          label={KIND_LABELS[kind]}
          items={grouped[kind]}
          selectedIds={filters[KIND_TO_FILTER_KEY[kind]]}
          onToggle={(id) => { toggle(kind, id); }}
        />
      ))}
      <TimeChips value={filters.maxTotalTimeMin} onSelect={selectTime} />
      {anySelected && (
        <button
          type="button"
          onClick={() => { onChange({ tagIds: [], categoryIds: [], cuisineIds: [], dietFlags: [] }); }}
          className="text-xs text-zinc-400 hover:text-zinc-200 underline underline-offset-2 ml-1"
        >
          Clear filters
        </button>
      )}
    </div>
  );
}
